import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Box, Chip, Grid, Card, CardContent, Button } from '@mui/material';

const ComicDetails = () => {
    const { comicId } = useParams(); // Lấy slug truyện từ URL
    const [comic, setComic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedChapter, setSelectedChapter] = useState(null); // State để lưu chapter đang được chọn
    const [hoveredChapter, setHoveredChapter] = useState(null); // State để lưu chapter đang hover
    const navigate = useNavigate(); // Sử dụng useNavigate để điều hướng

    // Lấy dữ liệu chi tiết truyện từ API
    const fetchComicDetails = async () => {
        try {
            const response = await axios.get(`https://otruyenapi.com/v1/api/truyen-tranh/${comicId}`);
            setComic(response.data.data.item);
        } catch (error) {
            console.error('Error fetching comic details:', error);
            setError('Không thể tải dữ liệu truyện.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComicDetails();
        window.scrollTo(0, 0);
    }, [comicId]);

    return (
        <Container className="app-container">
            {loading && <Typography>Đang tải dữ liệu...</Typography>} {/* Thông báo tải dữ liệu */}
            {error && <Typography>{error}</Typography>} {/* Thông báo lỗi */}
            {!loading && !comic && <Typography>Không tìm thấy truyện.</Typography>} {/* Thông báo không tìm thấy truyện */}

            {comic && ( // Nếu có dữ liệu truyện
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <div className="comic-thumbnail-wrapper" style={{ position: 'relative' }}>
                                <img
                                    className="comic-image"
                                    src={`https://img.otruyenapi.com/uploads/comics/${comic.thumb_url}`}
                                    alt={comic.name}
                                    style={{
                                        width: '100%', // Chiều rộng 100% để hình ảnh chiếm toàn bộ không gian
                                        height: '400px', // Chiều cao cố định
                                        objectFit: 'cover' // Đảm bảo hình ảnh không bị bóp méo
                                    }}
                                />
                                {/* Hiển thị trạng thái trên ảnh */}
                                <div className={`comic-status ${comic.status}`}>
                                    {comic.status === 'coming_soon' ? 'Sắp ra mắt' :
                                        comic.status === 'ongoing' ? 'Đang cập nhật' :
                                            comic.status === 'completed' ? 'Hoàn thành' :
                                                'Chưa có trạng thái'}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography style={{ fontSize: '2rem', fontWeight: 'bold' }}>{comic.name}</Typography>
                            <Typography className="comic-author">Tác giả: {comic.author || 'Đang cập nhật'}</Typography>
                            {/* Thêm Category vào đây */}
                            <Box mt={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Thể loại: {comic.category && comic.category.map((cat) => (
                                        <Chip key={cat.id} label={cat.name} style={{ margin: '0 4px' }} />
                                    ))}
                                </Typography>
                            </Box>
                            {/* Hiển thị ngày cập nhật */}
                            <Typography variant="body2" color="textSecondary" style={{ textAlign: 'right', marginTop: '7px' }}>
                                Ngày cập nhật: {new Date(comic.updatedAt).toLocaleDateString('vi-VN')}
                            </Typography>

                            <Box className="comic-genres" mt={1}>
                                {comic.genres && comic.genres.map((genre, index) => (
                                    <Chip key={index} label={genre.name} size="small" style={{ margin: 2 }} />
                                ))}
                            </Box>
                            {/* Box chứa nội dung truyện */}
                            <Box mt={2} p={2} border={1} borderColor="grey.400" borderRadius={2}>
                                <Typography variant="body1">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: comic.content.replace(/<\/?p[^>]*>/g, '') }}
                                    />
                                </Typography>
                            </Box>
                            {/* Thêm nút "Đọc từ đầu" và "Đọc tiếp" */}
                            <Box mt={2} display="flex" justifyContent="space-between">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate(`/chapters/${comic.chapters[0].server_data[0].chapter_api_data.split('/').pop()}?comicId=${comicId}`)}
                                    style={{ flexGrow: 1, marginRight: '8px' }} // Cung cấp khoảng cách giữa hai nút
                                >
                                    Đọc từ đầu
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        const lastChapter = comic.chapters[comic.chapters.length - 1].server_data[0];
                                        navigate(`/chapters/${lastChapter.chapter_api_data.split('/').pop()}?comicId=${comicId}`);
                                    }}
                                    style={{ flexGrow: 1, marginRight: '8px' }} // Cung cấp khoảng cách giữa hai nút
                                >
                                    Đọc tiếp
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        const lastChapter = comic.chapters[comic.chapters.length - 1].server_data[0];
                                        navigate(`/chapters/${lastChapter.chapter_api_data.split('/').pop()}?comicId=${comicId}`);
                                    }}
                                    style={{ flexGrow: 1 }} // Đảm bảo nút còn lại chiếm khoảng trống
                                >
                                    Yêu thích
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box mt={4}>
                        <Typography variant="h5" gutterBottom>
                            Danh Sách Chương
                        </Typography>
                        <hr style={{ margin: '20px 0', border: '1px solid #ccc' }} />
                        <Box style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '8px', padding: '10px' }}>
                            {comic.chapters && comic.chapters.map((chapter) => (
                                chapter.server_data.map((serverChapter) => (
                                    <Card
                                        key={serverChapter.chapter_api_data}
                                        variant="outlined"
                                        style={{
                                            marginBottom: '10px',
                                            backgroundColor: selectedChapter === serverChapter.chapter_api_data ? '#e3f2fd' : 'white', // Thay đổi màu nền cho chapter được chọn
                                            border: hoveredChapter === serverChapter.chapter_api_data ? '1px solid #1976d2' : '1px solid transparent' // Hiện viền cho chapter đang hover
                                        }}
                                        onMouseEnter={() => setHoveredChapter(serverChapter.chapter_api_data)} // Cập nhật chapter đang hover
                                        onMouseLeave={() => setHoveredChapter(null)} // Reset chapter đang hover
                                        onClick={() => {
                                            setSelectedChapter(serverChapter.chapter_api_data); // Cập nhật chapter được chọn
                                            navigate(`/chapters/${serverChapter.chapter_api_data.split('/').pop()}?comicId=${comicId}`); // Chuyển đến chương đã chọn
                                        }} // Chuyển đến chương khi nhấp
                                    >
                                        <CardContent>
                                            <Typography variant="body1">
                                                <Link
                                                    to={`/chapters/${serverChapter.chapter_api_data.split('/').pop()}?comicId=${comicId}`} // Lấy _id từ chapter_api_data và truyền comicId qua query
                                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                                >
                                                    {serverChapter.chapter_title ?
                                                        `Chương ${serverChapter.chapter_name} : ${serverChapter.chapter_title}` :
                                                        `Chương ${serverChapter.chapter_name}`
                                                    }
                                                </Link>
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                ))
                            ))}
                        </Box>
                    </Box>
                </>
            )}
        </Container>
    );
};

export default ComicDetails;
